import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, IconButton, Modal, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import moment from 'moment';
import { useToast } from 'use-toast-mui';

import CommonDatePicker from '../../../form-components/CommonDatePicker';
import ConfirmModal from '../../common/modal/ConfirmModal';
import {
  EditModalContainer,
  EditHeader,
  EditHeaderTitle,
  EditHeaderClose,
  EditBody,
  EditFooter,
  SummaryText,
  SummaryStrong,
  SummaryMuted,
  FooterActions,
  CancelButton,
  ApplyButton,
  FilterChip,
  EmptyMessage,
} from '../../member/modal/MemberRecipientPickerModalStyled';
import { ReserveMemberPicker } from './groupLessonTemplate/ReserveMemberSelectModal';
import TicketPickerDropdown from './groupLessonTemplate/TicketPickerDropdown';
import ReserveMemberResultList from './groupLessonTemplate/ReserveMemberResultList';
import { createGroupBatchReserve, getGroupBatchReserveClasses, previewGroupBatchReserve } from '../../../api/lesson';
import { errorMessages } from '../../../utils/methods';

const DAYS = [
  { value: 'MONDAY', label: '월' },
  { value: 'TUESDAY', label: '화' },
  { value: 'WEDNESDAY', label: '수' },
  { value: 'THURSDAY', label: '목' },
  { value: 'FRIDAY', label: '금' },
  { value: 'SATURDAY', label: '토' },
  { value: 'SUNDAY', label: '일' },
];
const DAY_KO = ['일', '월', '화', '수', '목', '금', '토']; // moment().day() 순서
const CLASS_CHIP_PREVIEW = 4; // 3단계 선택 수업 칩 — 접힌 상태에서 보이는 개수
const focusRingSx = {
  outline: 'none',
  boxShadow: '0 0 0 3px rgba(74,179,188,0.35)',
  '&:focus-visible, &:focus': { boxShadow: '0 0 0 4px rgba(74,179,188,0.5)' },
  animation: 'batchReservePulse 1.2s ease-in-out 2',
  '@keyframes batchReservePulse': {
    '0%, 100%': { boxShadow: '0 0 0 3px rgba(74,179,188,0.35)' },
    '50%': { boxShadow: '0 0 0 7px rgba(74,179,188,0.12)' },
  },
};
const disabledSx = { opacity: 0.5, pointerEvents: 'none' };
const labelSx = { fontSize: '13px', color: '#555', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 };
const STEPS = ['수업 선택', '회원 선택', '확인·예약'];

const remainingOf = c => (c.membersMaxCount == null ? Infinity : Math.max(0, c.membersMaxCount - (c.currentMemberCount || 0)));

const inputSx = {
  '& .MuiOutlinedInput-root': { height: '38px', backgroundColor: '#fff', borderRadius: '6px', fontSize: '13px' },
};
// 시간대 — 브라우저 기본 time 입력. MUI 필드 안에서 잘리지 않게 높이·패딩을 맞추고,
// 값이 없을 땐 브라우저의 "--:-- --" 대신 우리 placeholder 를 보인다(네이티브 표시는 투명 처리).
const timeSx = {
  '& .MuiOutlinedInput-root': { height: '38px', backgroundColor: '#fff', borderRadius: '6px', fontSize: '13px' },
  '& input': { padding: '0 6px', height: '38px', boxSizing: 'border-box', whiteSpace: 'nowrap', minWidth: 0 },
  // MUI 가 disabled 입력에 -webkit-text-fill-color 를 주므로 그것까지 투명으로.
  '& input.time-empty:not(:focus)::-webkit-datetime-edit': { color: 'transparent', WebkitTextFillColor: 'transparent' },
  '& input.time-empty:not(:focus)::-webkit-datetime-edit-fields-wrapper': { color: 'transparent', WebkitTextFillColor: 'transparent' },
};
// 비어 있고 포커스 없을 때만 안내문. 시계 아이콘을 눌러 포커스가 오면 브라우저 기본 표기(오전 --:--)가 드러나므로 안내문을 치운다.
const TimeField = ({ value, onChange, placeholder, disabled }) => {
  const [focused, setFocused] = useState(false);
  return (
    <Box sx={{ position: 'relative', width: '140px', flexShrink: 0 }}>
      <TextField
        size="small" type="time" value={value} disabled={disabled}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputProps={{ className: value ? '' : 'time-empty' }}
        sx={{ width: '100%', ...timeSx }}
      />
      {!value && !focused && (
        <Typography sx={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: disabled ? '#bbb' : '#999', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
          {placeholder}
        </Typography>
      )}
    </Box>
  );
};
const panelSx = {
  flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', backgroundColor: '#fff',
  border: '1px solid #d6dbdf', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 6px rgba(28,38,46,0.08)',
};

/**
 * 기존 그룹수업 × 여러 회원 일괄예약(백로그 #140) — 3단계 마법사.
 *
 * 1) 수강권(필수)·예약 횟수 N(필수)·기간(필수)·요일·시간대(선택)로 그 수강권을 쓰는 앞으로의 그룹수업을 찾아 정확히 N개 체크 → 다음
 *    수업마다 매핑 수강권이 달라도 한 상품으로 통일해 후보·판정이 어긋나지 않게 한다(사용자 확정).
 * 2) 회원 선택(생성 동시예약과 같은 피커, 후보 = 선택 수업 매핑 수강권 보유자, 상한 = 남은 정원 최소) → 다음
 * 3) 미리보기(회원 단위 전부-아니면-전무, 이미 예약 회차는 건너뜀) 확인 → 일괄예약. 앱 알림 없음.
 */
const GroupBatchReserveModal = ({ open, onClose, user, onRefresh, tickets = [] }) => {
  const toast = useToast();
  const branchId = user?.branch?.branchId;
  const token = user?.token;

  const [step, setStep] = useState(0);
  const [ticketIds, setTicketIds] = useState([]); // 1단계 수강권(필수, 일일권 제외) — 여러 개 고르면 전부 매핑된 수업만
  const [memberTicketIds, setMemberTicketIds] = useState({}); // 회원별로 쓸 상품(담긴 회원 패널에서 선택)
  const [multiTicketConfirmOpen, setMultiTicketConfirmOpen] = useState(false); // 다중 선택 조회 전 안내
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false); // 예약 실행 전 최종 확인
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reserveCount, setReserveCount] = useState(''); // 이번에 예약할 수업 수 — 이 수만큼 선택해야 다음으로
  const [days, setDays] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [classesExpanded, setClassesExpanded] = useState(false); // 3단계 선택 수업 칩 펼침
  const [minSeats, setMinSeats] = useState('');
  // 조회 조건이 바뀌면 이전 결과로 예약하지 않도록 목록·선택을 버린다(수강권 변경은 자체 핸들러에서 처리).
  const resetSearched = () => { if (classes !== null) { setClasses(null); changeSelection([]); } }; // 조회 결과 중 예약 가능 수(남은 정원)가 이 수 이상인 수업만. 선택 조건
  const [allTime, setAllTime] = useState(true); // 시간대 '전체' — 켜면 시각 입력을 비우고 잠근다
  const [nameQuery, setNameQuery] = useState('');

  const [classes, setClasses] = useState(null); // null = 아직 조회 안 함
  const [searching, setSearching] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [members, setMembers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  // 조회된 수업이 예약 횟수보다 적을 수 있다 — 적게 골라도 다음은 열어 두고, 예약 횟수를 선택 수로 내릴지 확인한다.
  const [shortfallConfirmOpen, setShortfallConfirmOpen] = useState(false);
  // 조회 요청 일련번호 — 닫았다 다시 열거나 재조회한 뒤에 도착한 옛 응답은 버린다(컴포넌트가 계속 마운트돼 있어 alive 플래그가 없다).
  const searchSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    searchSeq.current += 1; // 닫기 전에 나간 조회 응답은 무시
    setSearching(false);
    setStep(0);
    setStartDate(moment().toDate());
    setEndDate(moment().add(6, 'days').toDate());
    setReserveCount('');
    setTicketIds([]);
    setMemberTicketIds({});
    multiTicketAckRef.current = ''; // 다시 열면 다중 수강권 안내를 처음부터 다시 보여 준다
    setDays([]);
    setStartTime(''); setEndTime(''); setAllTime(true);
    setMinSeats('');
    setClassesExpanded(false);
    setNameQuery('');
    setClasses(null);
    setSelectedIds([]);
    setMembers([]);
    setPreview(null);
  }, [open]);

  // 수강권과 일일권은 상품 ID 체계가 달라 "C:<id>"·"T:<id>" 로 종류까지 값에 담는다.
  const ticketSelectOptions = useMemo(
    () => tickets.map(t => {
      const trial = t.ticketType === 'TRIAL_TICKET';
      return {
        value: `${trial ? 'T' : 'C'}:${t.courseTicketId}`,
        label: trial ? `[일일권] ${t.courseTicketName}` : t.courseTicketName,
        sortLabel: t.courseTicketName, // 정렬·ㄱㄴㄷ 헤더는 상품명 기준 — "[일일권]" 접두사가 특문 그룹으로 빠지지 않게
        hint: [t.usageCount ? `${t.usageCount}회` : null, t.usagePeriodDisplay || null].filter(Boolean).join(' · '),
      };
    }),
    [tickets],
  );
  const courseTicketIds = useMemo(
    () => ticketIds.filter(v => String(v).startsWith('C:')).map(v => Number(String(v).slice(2))),
    [ticketIds],
  );
  const trialProductIds = useMemo(
    () => ticketIds.filter(v => String(v).startsWith('T:')).map(v => Number(String(v).slice(2))),
    [ticketIds],
  );
  const selectedTickets = useMemo(
    () => ticketIds
      .map(v => {
        const trial = String(v).startsWith('T:');
        const id = String(v).slice(2);
        return tickets.find(t => String(t.courseTicketId) === id
          && (t.ticketType === 'TRIAL_TICKET') === trial);
      })
      .filter(Boolean),
    [tickets, ticketIds],
  );
  const visibleClasses = useMemo(() => {
    const seats = Number(minSeats) || 0;
    return (classes || [])
      .filter(c => !nameQuery || (c.className || '').toLowerCase().includes(nameQuery.toLowerCase()))
      .filter(c => seats <= 0 || remainingOf(c) >= seats);
  }, [classes, nameQuery, minSeats]);
  const selectedClasses = useMemo(() => (classes || []).filter(c => selectedIds.includes(c.courseClassId)), [classes, selectedIds]);
  // 상한 = 선택 수업들의 남은 정원 최소값(대기 없음). 이미 예약된 회원은 자리를 새로 쓰지 않으므로 백엔드가 회차별로 다시 확인한다.
  const minRemaining = useMemo(
    () => selectedClasses.reduce((min, c) => Math.min(min, remainingOf(c)), Infinity),
    [selectedClasses],
  );
  const targetCount = Number(reserveCount) || 0;
  // 예약 횟수를 줄여 선택이 넘치면 그만큼만 잘라낸다(늘릴 때는 고른 수업을 유지 — 선택 < 횟수도 허용된다).
  // 타이핑 중간값(12→1→15)에 선택이 잘리면 고른 수업이 통째로 날아간다 — 자동 절단은 하지 않고
  // 초과 상태면 "다음"에서 막고(안내), 부족하면 확인 모달에서 예약 횟수를 내린다.
  const setReserveCountAndReset = next => setReserveCount(next);
  // 조회 필수값(예약 횟수·기간)이 다 채워지고 아직 조회 전이면 "수업 조회"에 포커스를 줘 다음 동작을 알린다.
  const canSearch = ticketIds.length > 0 && targetCount > 0 && !!startDate && !!endDate;
  const searchButtonRef = useRef(null);
  const prevCanSearchRef = useRef(false);
  useEffect(() => {
    if (!prevCanSearchRef.current && canSearch && classes === null) searchButtonRef.current?.focus();
    prevCanSearchRef.current = canSearch;
  }, [canSearch, classes]);
  // 다음 버튼은 단계 조건을 채웠을 때만 활성 — 1단계는 예약 횟수만큼 정확히 선택, 2단계는 회원 1명 이상.
  const canNext = step === 0
    ? targetCount > 0 && selectedIds.length > 0 && selectedIds.length <= targetCount && minRemaining > 0
    : step === 1 ? members.length > 0 : false;

  // 회원 피커 칩 = 1단계에서 고른 수강권들.
  const ticketOptions = useMemo(
    () => selectedTickets.map(t => ({
      id: t.courseTicketId,
      name: t.ticketType === 'TRIAL_TICKET' ? `[일일권] ${t.courseTicketName}` : t.courseTicketName,
      ticketType: t.ticketType === 'TRIAL_TICKET' ? 'TRIAL' : 'COURSE',
    })),
    [selectedTickets],
  );

  // 수업 선택이 바뀌면 회원 선택·미리보기는 다시 해야 한다. 선택 갱신은 함수형으로(연타해도 이전 상태를 덮지 않게).
  const changeSelection = updater => {
    setSelectedIds(updater);
    setPreview(null);
    setMemberTicketIds({}); // 수업이 바뀌면 회원별로 고른 상품도 무효 — 낡은 상품을 요청에 실어 보내지 않는다
    if (members.length > 0) {
      setMembers([]);
      toast.info('선택 수업이 바뀌어 회원 선택을 초기화했습니다');
    }
  };

  // 수강권을 여러 개 고르면 "모든 수강권이 매핑된 수업만" 조회된다는 걸 먼저 확인받는다(한 번 확인하면 선택이 바뀔 때까지 다시 묻지 않는다).
  const multiTicketAckRef = useRef(''); // 이번에 안내를 이미 본 수강권 조합(모달을 닫으면 잊는다)
  const requestSearch = () => {
    const key = [...ticketIds].sort().join(',');
    if (ticketIds.length > 1 && multiTicketAckRef.current !== key) {
      setMultiTicketConfirmOpen(true);
      return;
    }
    search();
  };
  const confirmMultiTicket = () => {
    multiTicketAckRef.current = [...ticketIds].sort().join(',');
    setMultiTicketConfirmOpen(false);
    search();
  };

  const search = async () => {
    if (!startDate || !endDate) { toast.error('기간을 입력해 주세요'); return; }
    // 기본 시작일은 now(시각 포함), 직접 고른 날짜는 자정이라 날짜 단위로 비교해야 같은 날이 통과한다.
    if (moment(startDate).startOf('day').isAfter(moment(endDate).startOf('day'))) { toast.error('종료일이 시작일보다 빠릅니다'); return; }
    if (ticketIds.length === 0) { toast.error('수강권을 선택해 주세요'); return; }
    if (!Number.isInteger(targetCount) || targetCount <= 0) { toast.error('예약 횟수를 입력해 주세요'); return; }
    if ((startTime && !endTime) || (!startTime && endTime)) { toast.error('시간대는 시작·종료를 함께 입력해 주세요'); return; }
    if (startTime && endTime && startTime > endTime) { toast.error('종료 시간이 시작 시간보다 빠릅니다'); return; }
    const seq = ++searchSeq.current;
    setSearching(true);
    try {
      const data = await getGroupBatchReserveClasses({
        branchId,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        dayOfWeeks: days,
        startTime: startTime || null,
        endTime: endTime || null,
      }, token);
      if (seq !== searchSeq.current) return;
      // 고른 상품을 "전부" 예약 가능 상품으로 가진 수업만 — 하나라도 빠지면 그 수업은 조회되지 않는다.
      setClasses((data || []).filter(c =>
        courseTicketIds.every(tid => (c.ticketIds || []).some(id => String(id) === String(tid)))
        && trialProductIds.every(tid => (c.trialTicketIds || []).some(id => String(id) === String(tid)))));
      changeSelection([]);
    } catch (error) {
      if (seq === searchSeq.current) toast.error(errorMessages(error));
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  };

  // 예약 횟수만큼만 고른다. 넘치면 막고 안내. 예약 횟수를 지웠으면 먼저 넣게 한다.
  const toggleClass = id => {
    if (targetCount <= 0) { toast.error('예약 횟수를 먼저 입력해 주세요'); return; }
    if (!selectedIds.includes(id) && selectedIds.length >= targetCount) {
      toast.error(`예약 횟수(${targetCount}회)만큼 이미 선택했습니다. 다른 수업을 빼고 고르거나 예약 횟수를 늘려 주세요`);
      return;
    }
    changeSelection(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };
  const visibleIds = visibleClasses.filter(c => remainingOf(c) > 0).map(c => c.courseClassId);
  // 전체 선택 = 보이는 수업을 위에서부터 예약 횟수까지. 필터로 가려진 채 이미 선택된 수업은 그대로 둔다.
  const hiddenSelected = selectedIds.filter(id => !visibleIds.includes(id));
  const fillIds = targetCount > hiddenSelected.length ? visibleIds.slice(0, targetCount - hiddenSelected.length) : [];
  const anyVisibleSelected = selectedIds.some(id => visibleIds.includes(id));
  const allVisibleSelected = fillIds.length > 0 && fillIds.every(id => selectedIds.includes(id));
  // 보이는 수업 중 하나라도 선택돼 있으면 해제(사용자가 고른 조합을 앞에서부터 N개로 바꿔치기하지 않는다), 없으면 위에서부터 채운다.
  const toggleAll = () => {
    if (anyVisibleSelected) { changeSelection(hiddenSelected); return; }
    if (targetCount <= 0) { toast.error('예약 횟수를 먼저 입력해 주세요'); return; }
    if (fillIds.length === 0) { toast.error(`예약 횟수(${targetCount}회)만큼 이미 선택했습니다`); return; }
    changeSelection([...hiddenSelected, ...fillIds]);
  };

  // 회원별로 고른 상품을 종류별 맵으로 나눈다(백엔드는 수강권·일일권을 따로 받는다).
  const memberPickPayload = () => {
    const course = {};
    const trial = {};
    Object.entries(memberTicketIds).forEach(([memberId, pick]) => {
      if (!pick) return;
      if (pick.ticketType === 'TRIAL') trial[memberId] = pick.productId;
      else course[memberId] = pick.productId;
    });
    return { memberCourseTicketIds: course, memberTrialTicketIds: trial };
  };

  const runPreview = async (memberList = members) => {
    setPreviewing(true);
    try {
      const data = await previewGroupBatchReserve({
        courseClassIds: selectedIds, memberIds: memberList.map(m => m.memberId),
        courseTicketIds, trialTicketIds: trialProductIds, ...memberPickPayload(),
      }, token);
      setPreview(data);
    } catch (error) {
      toast.error(errorMessages(error));
      setStep(1);
    } finally {
      setPreviewing(false);
    }
  };

  // 3단계에서 회원을 빼면 남은 회원으로 다시 판정한다(정원 자리가 바뀔 수 있음). 다 빼면 회원 선택으로 돌아간다.
  const removeMember = memberId => {
    const next = members.filter(m => m.memberId !== memberId);
    setMembers(next);
    setPreview(null);
    if (next.length === 0) { setStep(1); return; }
    runPreview(next);
  };

  const goNext = () => {
    if (step === 0) {
      if (targetCount <= 0) { toast.error('예약 횟수를 입력해 주세요'); return; }
      if (selectedIds.length === 0) { toast.error('수업을 선택해 주세요'); return; }
      if (selectedIds.length > targetCount) {
        toast.error(`예약 횟수(${targetCount}회)만큼만 선택할 수 있습니다. 지금 ${selectedIds.length}개`);
        return;
      }
      if (minRemaining <= 0) { toast.error('선택한 수업 중 남은 정원이 없는 수업이 있습니다'); return; }
      if (selectedIds.length < targetCount) {
        setShortfallConfirmOpen(true); // 예약 횟수를 선택 수로 내리고 진행할지 확인
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (members.length === 0) { toast.error('회원을 선택해 주세요'); return; }
      setPreview(null);
      setStep(2);
      runPreview();
    }
  };

  // 확인 후: 예약 횟수를 선택한 수업 수로 내리고 2단계로.
  const confirmShortfall = () => {
    setReserveCount(String(selectedIds.length));
    setShortfallConfirmOpen(false);
    setStep(1);
  };

  const excludedCount = preview ? preview.members.filter(m => m.failures.length > 0).length : 0;
  const reservableCount = preview ? preview.members.filter(m => m.failures.length === 0 && m.reservedCount > 0).length : 0;

  const submit = async () => {
    setSubmitConfirmOpen(false);
    setSaving(true);
    try {
      const res = await createGroupBatchReserve({
        courseClassIds: selectedIds, memberIds: members.map(m => m.memberId),
        courseTicketIds, trialTicketIds: trialProductIds, ...memberPickPayload(),
      }, token);
      const excluded = (res?.members || []).filter(m => (m.failures || []).length > 0).length;
      if (excluded > 0) toast.warning(`회원 ${res.reservedMemberCount}명 예약 완료. 예약 불가 ${excluded}명은 제외되었습니다`);
      else toast.success(`회원 ${res?.reservedMemberCount ?? 0}명을 선택한 ${selectedIds.length}개 수업에 예약했습니다`);
      onRefresh?.('skip');
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setSaving(false);
    }
  };

  const busy = previewing || saving;
  const close = () => { if (!busy) onClose(); };

  const renderStepper = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', ml: '16px' }}>
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', opacity: i === step ? 1 : 0.5 }}>
            <Box sx={{
              width: '18px', height: '18px', borderRadius: '50%', fontSize: '11px', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: i <= step ? '#4AB3BC' : 'rgba(255,255,255,0.25)', color: '#fff',
            }}>{i + 1}</Box>
            <Typography sx={{ fontSize: '12.5px', fontWeight: i === step ? 700 : 500, color: '#fff' }}>{label}</Typography>
          </Box>
          {i < STEPS.length - 1 && <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>›</Typography>}
        </React.Fragment>
      ))}
    </Box>
  );

  const renderStep1 = () => (
    <EditBody sx={{ flexDirection: 'column', gap: '12px' }}>
      {/* 조건 패널 — 두 줄 고정. 1줄: 수강권 / 예약 횟수 / 기간, 2줄: 요일 … 시간대(오른쪽) / 조회 버튼. */}
      <Box sx={{ ...panelSx, flex: 'none', overflow: 'visible', p: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={labelSx}>수강권</Typography>
          <TicketPickerDropdown
            multiple
            value={ticketIds}
            onChange={v => { setTicketIds(v); setClasses(null); setMemberTicketIds({}); changeSelection([]); }}
            options={ticketSelectOptions}
            width="320px"
          />
          <Typography sx={{ ...labelSx, ml: '14px' }}>예약 횟수</Typography>
          {/* 개인레슨 수업정보의 '차감 횟수'와 같은 스테퍼 — 인풋 안 −/+ 로 조절, 최소 1 */}
          <TextField
            size="small" type="text" inputMode="numeric" placeholder="0"
            value={reserveCount}
            onChange={e => setReserveCountAndReset(e.target.value.replace(/[^0-9]/g, ''))}
            InputProps={{
              startAdornment: (
                <IconButton size="small" aria-label="예약 횟수 줄이기" disabled={targetCount <= 1}
                  onClick={() => setReserveCountAndReset(String(Math.max(1, targetCount - 1)))} sx={{ p: '2px', mr: '2px' }}>
                  <RemoveIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              ),
              endAdornment: (
                <>
                  <span style={{ color: '#999', fontSize: '13px', marginRight: '2px' }}>회</span>
                  <IconButton size="small" aria-label="예약 횟수 늘리기"
                    onClick={() => setReserveCountAndReset(String(targetCount + 1))} sx={{ p: '2px' }}>
                    <AddIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </>
              ),
            }}
            inputProps={{ style: { textAlign: 'center', padding: '8px 0' } }}
            sx={{ width: '128px', ...inputSx, '& .MuiOutlinedInput-root': { height: '38px', backgroundColor: '#fff', borderRadius: '6px', fontSize: '13px', pl: '4px', pr: '4px' } }}
          />
          <Typography sx={{ ...labelSx, ml: '14px' }}>기간</Typography>
          <CommonDatePicker value={startDate} onChange={d => { setStartDate(d); resetSearched(); }} placeholder="시작일" sx={{ width: '164px' }} />
          <Typography sx={{ fontSize: '13px', color: '#999' }}>~</Typography>
          <CommonDatePicker value={endDate} onChange={d => { setEndDate(d); resetSearched(); }} minDate={startDate} placeholder="종료일" sx={{ width: '164px' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={labelSx}>요일</Typography>
          <Box sx={{ display: 'flex', gap: '4px' }}>
            {/* '전체' = 요일 조건 없음(비우기). 요일을 하나라도 고르면 그 요일만 조회. */}
            <FilterChip active={days.length === 0 || days.length === 7} style={{ padding: '0 10px', height: '30px' }} onClick={() => { setDays([]); resetSearched(); }}>
              전체
            </FilterChip>
            {DAYS.map(d => (
              <FilterChip key={d.value} active={days.length !== 7 && days.includes(d.value)} style={{ padding: '0 10px', height: '30px' }}
                onClick={() => {
                  setDays(prev => {
                    // 7개 전부 고른 상태는 '전체'와 같다 — 이때 한 요일을 누르면 그 요일만 남긴다(빼기가 아니라 좁히기).
                    if (prev.length === 7) return [d.value];
                    return prev.includes(d.value) ? prev.filter(x => x !== d.value) : [...prev, d.value];
                  });
                  resetSearched();
                }}>
                {d.label}
              </FilterChip>
            ))}
          </Box>
          {/* 시간대 — 오른쪽 정렬. '전체' 체크 = 시간 조건 없음(시각 입력 잠금). 시각을 넣으면 자동 해제. */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={labelSx}>시간대</Typography>
            <Box onClick={() => { const next = !allTime; setAllTime(next); if (next) { setStartTime(''); setEndTime(''); } resetSearched(); }}
              sx={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer', mr: '2px' }}>
              <Checkbox checked={allTime} size="small" sx={{ p: 0, color: '#c4c4c4', '&.Mui-checked': { color: '#4AB3BC' } }} />
              <Typography sx={{ fontSize: '13px', color: '#555', whiteSpace: 'nowrap' }}>전체</Typography>
            </Box>
            <TimeField value={startTime} placeholder="시작 시간" disabled={allTime} onChange={v => { setStartTime(v); if (v) setAllTime(false); resetSearched(); }} />
            <Typography sx={{ fontSize: '13px', color: '#999' }}>~</Typography>
            <TimeField value={endTime} placeholder="종료 시간" disabled={allTime} onChange={v => { setEndTime(v); if (v) setAllTime(false); resetSearched(); }} />
          </Box>
          <Box>
            <ApplyButton
              ref={searchButtonRef}
              tabIndex={0}
              onClick={requestSearch}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); requestSearch(); } }}
              sx={{
                height: '36px', minWidth: '96px', fontSize: '13px', opacity: searching ? 0.6 : 1,
                ...(canSearch && classes === null && !searching ? focusRingSx : { outline: 'none' }),
              }}>
              {searching ? '조회 중...' : '수업 조회'}
            </ApplyButton>
          </Box>
        </Box>
      </Box>

      <Box sx={panelSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '12px', py: '8px', backgroundColor: '#f7f8fa', borderBottom: '1px solid #eceff1' }}>
          <Checkbox size="small" checked={allVisibleSelected} indeterminate={anyVisibleSelected && !allVisibleSelected} onChange={toggleAll} disabled={visibleIds.length === 0}
            sx={{ p: 0, color: '#c4c4c4', '&.Mui-checked': { color: '#4AB3BC' }, '&.MuiCheckbox-indeterminate': { color: '#4AB3BC' } }} />
          {classes && (
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
              수업 {classes.length}개 · 선택 <span style={{ color: selectedIds.length === targetCount ? '#4AB3BC' : '#C2403D' }}>{selectedIds.length}</span>/{targetCount}개
            </Typography>
          )}
          {selectedIds.length > 0 && (
            <Typography sx={{ fontSize: '12px', color: '#888' }}>· 남은 정원 최소 {minRemaining === Infinity ? '무제한' : `${minRemaining}명`}</Typography>
          )}
          {/* 조회 결과 안에서 거른다 — 예약 가능 수(남은 정원)가 이 수 이상인 수업만. 비우면 전체. */}
          <Typography sx={{ ml: 'auto', fontSize: '12.5px', color: '#555', whiteSpace: 'nowrap' }}>남은 예약 자리 수</Typography>
          <TextField
            size="small" type="text" inputMode="numeric" placeholder="전체" value={minSeats}
            onChange={e => setMinSeats(e.target.value.replace(/[^0-9]/g, ''))}
            inputProps={{ style: { textAlign: 'center', padding: '4px 0' } }}
            sx={{ width: '64px', '& .MuiOutlinedInput-root': { height: '30px', fontSize: '12.5px', backgroundColor: '#fff' } }}
          />
          <Typography sx={{ fontSize: '12.5px', color: '#777', whiteSpace: 'nowrap', ml: '-4px' }}>명 이상</Typography>
          <TextField size="small" placeholder="수업명 검색" value={nameQuery} onChange={e => setNameQuery(e.target.value)}
            sx={{ ml: '8px', width: '200px', '& .MuiOutlinedInput-root': { height: '30px', fontSize: '12.5px', backgroundColor: '#fff' } }} />
        </Box>
        {/* 빈 안내는 정중앙보다 살짝 위가 눈에 먼저 들어온다 — 아래 여백으로 끌어올린다. */}
        <Box sx={{
          flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column',
          justifyContent: !classes || visibleClasses.length === 0 ? 'center' : 'flex-start',
          pb: !classes || visibleClasses.length === 0 ? '96px' : 0,
        }}>
          {!classes ? (
            <EmptyMessage>수강권·예약 횟수·기간을 정하고 "수업 조회"를 눌러 주세요</EmptyMessage>
          ) : classes.length === 0 ? (
            <EmptyMessage>이 수강권으로 예약할 수 있는 그룹수업이 기간 안에 없습니다</EmptyMessage>
          ) : visibleClasses.length === 0 ? (
            <EmptyMessage>검색과 일치하는 수업이 없습니다</EmptyMessage>
          ) : (
            /* 스케줄 일괄수정 표와 같은 컬럼. 상태 자리에 차감(빨강). */
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  {['', '날짜', '요일', '시간', '강의실', '수업명', '예약/정원', '강사', '차감'].map((label, i) => (
                    <th key={i} style={{
                      padding: '9px 10px', fontSize: '13px', fontWeight: 600, color: '#444',
                      borderBottom: '2px solid #4AB3BC', textAlign: 'center',
                      backgroundColor: '#f0f8f8', whiteSpace: 'nowrap',
                    }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleClasses.map(c => {
                  const checked = selectedIds.includes(c.courseClassId);
                  const full = remainingOf(c) <= 0;
                  const dayOfWeek = moment(c.classDate).day();
                  const td = {
                    padding: '8px 8px', fontSize: '13px', borderBottom: '1px solid #eee',
                    textAlign: 'center', whiteSpace: 'nowrap', color: full ? '#bbb' : '#333',
                  };
                  return (
                    <tr key={c.courseClassId}
                      style={{ backgroundColor: checked ? '#EDF7F7' : full ? '#fafafa' : 'transparent', cursor: full ? 'not-allowed' : 'pointer', opacity: full ? 0.7 : 1 }}
                      onMouseEnter={e => { if (!checked) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                      onMouseLeave={e => { if (!checked) e.currentTarget.style.backgroundColor = full ? '#fafafa' : 'transparent'; }}
                      onClick={() => { if (!full) toggleClass(c.courseClassId); }}>
                      <td style={td}><Checkbox size="small" checked={checked} disabled={full} sx={{ p: 0, color: '#bbb', '&.Mui-checked': { color: '#4AB3BC' } }} /></td>
                      <td style={{ ...td, fontWeight: 500 }}>{moment(c.classDate).format('MM.DD')}</td>
                      <td style={{ ...td, fontWeight: 700, color: full ? '#bbb' : dayOfWeek === 0 ? '#ef5350' : dayOfWeek === 6 ? '#2196f3' : '#333' }}>{DAY_KO[dayOfWeek]}</td>
                      <td style={{ ...td, color: full ? '#bbb' : '#555' }}>{c.startTime} ~ {c.endTime}</td>
                      <td style={{ ...td, color: full ? '#ccc' : '#666' }}>{c.roomName || '-'}</td>
                      <td style={{ ...td, fontWeight: 600, color: full ? '#bbb' : '#111', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.className}>{c.className}</td>
                      {/* 예약/정원 — 예약이 없으면 0/12. 정원 없으면 '무제한' */}
                      <td style={{ ...td, color: full ? '#C2403D' : td.color }}>
                        {c.membersMaxCount == null ? '무제한' : `${c.currentMemberCount}/${c.membersMaxCount}`}{full ? ' 마감' : ''}
                      </td>
                      <td style={{ ...td, color: full ? '#bbb' : '#555' }}>{c.trainerName || '-'}</td>
                      <td style={{ ...td, fontWeight: 700, color: '#C2403D' }}>{c.deductionCount}회</td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          )}
        </Box>
      </Box>
    </EditBody>
  );

  const statSx = { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '96px', px: '14px', py: '8px', borderRadius: '8px', backgroundColor: '#f7f8fa' };
  const statLabelSx = { fontSize: '11.5px', color: '#8c8c8c', fontWeight: 600 };
  const statValueSx = { fontSize: '18px', fontWeight: 700, color: '#262626', lineHeight: 1.3 };

  const renderStep3 = () => (
    <EditBody sx={{ flexDirection: 'column', gap: '12px' }}>
      <Box sx={{ ...panelSx, flex: 'none', p: '12px 16px', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
        {/* 숫자 블록 — 수업 / 회원 / 예약가능 / 제외 */}
        <Box sx={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <Box sx={statSx}><Typography sx={statLabelSx}>수업</Typography><Typography sx={statValueSx}>{selectedIds.length}<span style={{ fontSize: 12, marginLeft: 2 }}>개</span></Typography></Box>
          <Box sx={statSx}><Typography sx={statLabelSx}>회원</Typography><Typography sx={statValueSx}>{members.length}<span style={{ fontSize: 12, marginLeft: 2 }}>명</span></Typography></Box>
          <Box sx={{ ...statSx, backgroundColor: '#E4F5F6' }}><Typography sx={{ ...statLabelSx, color: '#0F7F85' }}>예약가능</Typography><Typography sx={{ ...statValueSx, color: '#0F7F85' }}>{preview ? reservableCount : '-'}<span style={{ fontSize: 12, marginLeft: 2 }}>명</span></Typography></Box>
          <Box sx={{ ...statSx, backgroundColor: excludedCount > 0 ? '#FDF0EF' : '#f7f8fa' }}><Typography sx={{ ...statLabelSx, color: excludedCount > 0 ? '#C2403D' : '#8c8c8c' }}>제외</Typography><Typography sx={{ ...statValueSx, color: excludedCount > 0 ? '#C2403D' : '#262626' }}>{preview ? excludedCount : '-'}<span style={{ fontSize: 12, marginLeft: 2 }}>명</span></Typography></Box>
        </Box>
        {/* 선택 수업 — 칩으로 나열 */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* 이름을 다 늘어놓으면 줄이 넘친다 — 대표 하나 + "외 N개"(전체는 툴팁). */}
          <Typography title={selectedTickets.map(t => t.courseTicketName).join(', ')}
            sx={{ fontSize: '11.5px', color: '#8c8c8c', fontWeight: 600, mb: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedTickets.length === 0 ? '선택 수업'
              : selectedTickets.length === 1 ? `${selectedTickets[0].courseTicketName} 으로 예약`
                : `${selectedTickets[0].courseTicketName} 외 ${selectedTickets.length - 1}개로 예약`}
          </Typography>
          {/* 수업이 많으면 앞 몇 개만 보이고 "+N개 더보기"로 펼친다(펼치면 스크롤). */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', maxHeight: classesExpanded ? '96px' : 'none', overflowY: classesExpanded ? 'auto' : 'hidden' }}>
            {(classesExpanded ? selectedClasses : selectedClasses.slice(0, CLASS_CHIP_PREVIEW)).map(c => (
              <Box key={c.courseClassId} component="span" title={c.className}
                sx={{ fontSize: '12px', color: '#333', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '6px', px: '8px', py: '2px', whiteSpace: 'nowrap', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <b>{moment(c.classDate).format('MM-DD')} ({DAY_KO[moment(c.classDate).day()]}) {c.startTime}</b> · {c.className}
              </Box>
            ))}
            {selectedClasses.length > CLASS_CHIP_PREVIEW && (
              <Typography onClick={() => setClassesExpanded(prev => !prev)}
                sx={{ fontSize: '12px', fontWeight: 600, color: '#0F7F85', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
                {classesExpanded ? '접기' : `+${selectedClasses.length - CLASS_CHIP_PREVIEW}개 더보기`}
              </Typography>
            )}
          </Box>
          {preview && excludedCount > 0 && (
            <Typography sx={{ fontSize: '12px', color: '#C2403D', mt: '8px' }}>
              모든 수업에 예약 가능한 회원만 예약되며, 불가 회원은 통째로 제외됩니다. 이미 예약된 수업은 건너뜁니다.
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={panelSx}>
        <Box sx={{ px: '16px', py: '8px', backgroundColor: '#f7f8fa', borderBottom: '1px solid #eceff1', fontSize: '12px', fontWeight: 700, color: '#8c8c8c' }}>
          회원별 판정
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <ReserveMemberResultList members={preview?.members} loading={previewing || !preview} onRemove={removeMember} />
        </Box>
      </Box>
    </EditBody>
  );

  return (
    <>
    <ConfirmModal
      open={submitConfirmOpen}
      onClose={() => setSubmitConfirmOpen(false)}
      onConfirm={submit}
      message="선택한 회원을 예약할까요?"
      highlightText="__none__"
      detail={
        // ConfirmModal 제목의 marginBottom 24px 를 상쇄해 아래 요소를 붙인다.
        <Box sx={{ mt: '-14px', mb: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '18px', py: '10px', borderRadius: '8px', backgroundColor: '#f7f8fa' }}>
            <Typography sx={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 600 }}>수업</Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#262626' }}>{selectedIds.length}개</Typography>
            <Typography sx={{ fontSize: '13px', color: '#bbb' }}>×</Typography>
            <Typography sx={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 600 }}>회원</Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0F7F85' }}>{reservableCount}명</Typography>
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#737373', textAlign: 'center', lineHeight: 1.6, whiteSpace: 'nowrap' }}>
            {excludedCount > 0 ? <>예약 불가 {excludedCount}명은 제외하고 진행합니다.<br /></> : null}
            이미 예약된 회차는 건너뜁니다. 앱 알림은 보내지 않습니다.
          </Typography>
        </Box>
      }
      cancelText="취소"
      confirmText="예약"
      confirmColor="#4AB3BC"
    />
    <ConfirmModal
      open={multiTicketConfirmOpen}
      onClose={() => setMultiTicketConfirmOpen(false)}
      onConfirm={confirmMultiTicket}
      message="수강권을 여러 개 선택했습니다."
      highlightText="__none__"
      detail={
        // ConfirmModal 의 제목이 marginBottom 24px 라 그만큼 끌어올려 라벨을 제목에 붙인다.
        <Box sx={{ mt: '-14px', mb: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
          {/* 이름을 다 늘어놓으면 모달이 깨진다 — 대표 하나 + "외 N개"(전체는 툴팁). */}
          <Box component="span" title={selectedTickets.map(t => t.courseTicketName).join(', ')}
            sx={{ fontSize: '12.5px', color: '#0F7F85', backgroundColor: '#E4F5F6', borderRadius: '6px', px: '10px', py: '3px', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedTickets[0]?.courseTicketName}
            {selectedTickets.length > 1 && ` 외 ${selectedTickets.length - 1}개`}
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#737373', textAlign: 'center', lineHeight: 1.6, whiteSpace: 'nowrap' }}>
            선택한 수강권을 <b style={{ color: '#333' }}>모두</b> 가진 수업만 조회됩니다.
            <br />하나라도 빠진 수업은 목록에서 빠집니다.
          </Typography>
        </Box>
      }
      cancelText="취소"
      confirmText="조회"
      confirmColor="#4AB3BC"
    />
    <ConfirmModal
      open={shortfallConfirmOpen}
      onClose={() => setShortfallConfirmOpen(false)}
      onConfirm={confirmShortfall}
      message="선택한 수업이 예약 횟수보다 적습니다."
      highlightText="__none__"
      detail={
        <Box sx={{ mt: '-14px', mb: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '18px', py: '10px', borderRadius: '8px', backgroundColor: '#f7f8fa' }}>
            <Typography sx={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 600 }}>예약 횟수</Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#999', textDecoration: 'line-through' }}>{targetCount}회</Typography>
            <Typography sx={{ fontSize: '18px', color: '#bbb' }}>→</Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#0F7F85' }}>{selectedIds.length}회</Typography>
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#737373', textAlign: 'center', lineHeight: 1.6 }}>
            선택한 {selectedIds.length}개 수업으로 진행하면
            <br />예약 횟수가 {selectedIds.length}회로 바뀝니다.
          </Typography>
        </Box>
      }
      cancelText="취소"
      confirmText="진행"
      confirmColor="#4AB3BC"
    />
    <Modal open={open} onClose={close}>
      <EditModalContainer sx={{ height: '680px' }}>
        <EditHeader>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditHeaderTitle>그룹수업 일괄예약</EditHeaderTitle>
            {renderStepper()}
          </Box>
          <EditHeaderClose onClick={close}>✕</EditHeaderClose>
        </EditHeader>

        {step === 0 && renderStep1()}
        {step === 1 && (
          <ReserveMemberPicker
            open={step === 1}
            token={token}
            branchId={branchId}
            courseClassIds={selectedIds}
            courseTicketIds={courseTicketIds}
            trialTicketIds={trialProductIds}
            memberTicketIds={memberTicketIds}
            onMemberTicketChange={(memberId, productId, ticketType) => {
              setMemberTicketIds(prev => ({ ...prev, [memberId]: { productId, ticketType } }));
              setPreview(null);
            }}
            ticketOptions={ticketOptions}
            maxCount={minRemaining === Infinity ? null : minRemaining}
            limitLabel="선택한 수업들의 최소 남은 예약 자리 수"
            picked={members}
            onPickedChange={next => { setMembers(next); setPreview(null); }}
          />
        )}
        {step === 2 && renderStep3()}

        <EditFooter>
          <SummaryText>
            {step === 0 && (
              <>
                <SummaryStrong>수업 {selectedIds.length}/{targetCount}개</SummaryStrong>
                {selectedIds.length > 0 && minRemaining !== Infinity && <SummaryMuted>· 최대 {minRemaining}명 예약 가능</SummaryMuted>}
              </>
            )}
            {step === 1 && (
              <>
                <SummaryStrong>{members.length}명</SummaryStrong>
                {minRemaining !== Infinity && <SummaryMuted>/ 최대 {minRemaining}명</SummaryMuted>}
                <SummaryMuted>· 선택한 {selectedIds.length}개 수업에 예약됩니다</SummaryMuted>
              </>
            )}
            {step === 2 && preview && (
              <>
                <SummaryStrong>예약 {reservableCount}명</SummaryStrong>
                {excludedCount > 0 && <SummaryMuted>· 제외 {excludedCount}명</SummaryMuted>}
              </>
            )}
          </SummaryText>
          <FooterActions>
            <CancelButton onClick={step === 0 ? close : () => setStep(step - 1)} sx={busy ? disabledSx : {}}>
              {step === 0 ? '취소' : '이전'}
            </CancelButton>
            {step < 2 ? (
              <ApplyButton onClick={goNext} sx={canNext ? {} : disabledSx}>다음</ApplyButton>
            ) : (
              <ApplyButton
                onClick={() => setSubmitConfirmOpen(true)}
                sx={!preview || busy || reservableCount === 0 ? disabledSx : {}}>
                {saving ? '예약 중...' : excludedCount > 0 ? '예약 불가 제외 후 일괄예약' : '일괄예약'}
              </ApplyButton>
            )}
          </FooterActions>
        </EditFooter>
      </EditModalContainer>
    </Modal>
    </>
  );
};

export default GroupBatchReserveModal;
